import { Route } from 'vue-router';
import app from '@/main';

/**
 * Fetches current route from app instance. If app instance doesn't exist, returns a default "empty route".
 * @returns `Route`
 */
export const getCurrentRoute = (): Route => (
  app()?.$route ?? {
    path: '',
    params: {},
    query: {},
    hash: '',
    fullPath: '',
    matched: [],
  });

/**
 * Current route path starts with the provided `path` input (including first `/`). Example input: `'/properties'`
 * @param path path string to assess
 * @returns boolean
 */
export const routeStartsWith = (path: string) => getCurrentRoute().path.startsWith(path);

/**
 * Current route path includes the provided `path` input.
 * @param path path string to assess
 * @returns boolean
 */
export const routeIncludes = (path: string) => getCurrentRoute().path.includes(path);

/**
 * Current route path ends with the provided `path` input.
 * @param path path string to assess
 * @returns boolean
 */
export const routeEndsWith = (path: string) => getCurrentRoute().path.endsWith(path);

/**
 * Checks for existence of a specific route parameter key.
 * @param paramKey the key in `this.$route.params` that we assess.
 * @returns boolean
 */
export const routeParamExists = (paramKey: string) => !!getCurrentRoute().params[paramKey];

/**
 * Checks for existence of a specific query parameter key.
 * @param queryKey the key in `this.$route.query` that we assess.
 * @returns boolean
 */
export const routeQueryKeyExists = (queryKey: string) => !!getCurrentRoute().query[queryKey];

/**
 * Checks for existence of a specific value for a specific query parameter key.
 * @param queryKey the key in `this.$route.query` that we assess.
 * @param value the value `queryKey` should equal.
 * @returns boolean
 */
export const routeQueryKeyEquals = (queryKey: string, value: string) => getCurrentRoute().query[queryKey] === value;

/**
 * Just a regular regex check against a given path. In this case, `this.$route.fullPath` is used.
 * @param regex The `RegExp` pattern to assess.
 * @returns boolean
 */
export const routeMatchesRegex = (regex: RegExp) => !!getCurrentRoute().fullPath.match(regex);
